import { Component, OnInit, inject } from '@angular/core';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { CrearReglaVehiculoComponent } from '../crear-regla-vehiculo/crear-regla-vehiculo.component';
import { IActivo, TipoValoresReporte } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { IParamEventoUsuario } from '../../../../standalone/config-evento-usuario/crear-editar-config-evento-usuario-dialog/crear-editar-config-evento-usuario-dialog.component';
import { IParamCertificadoEntidad } from '../../../../standalone/certificado-entidad/crear-editar-certificado-entidad/crear-editar-certificado-entidad.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { etiquetaTipoYFuncion } from '../../etiquetas-vehiculo';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { MapaVehiculosService } from '../../contenedor-mapa/mapaVehiculos.service';
import { IPageTitleChip } from '../../../../../componentes/page-title/page-title.component';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { RecorridosDinamicosService } from '../../../../../auxiliares/servicios/http/recorridos-dinamicos.service';

@Component({
  selector: 'app-detalles-vehiculo',
  templateUrl: './detalles-vehiculo.component.html',
  styleUrls: [
    '../../../../../componentes/detalle/detalle-pagina.scss',
    './detalles-vehiculo.component.scss',
  ],
  standalone: false,
})
export class DetallesVehiculoComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lista2 = inject(Lista2Service);
  private paramsService = inject(ParamsService);
  private mapaService = inject(MapaVehiculosService);
  private formularios = inject(FormularioDialogService);
  private dialogService = inject(DialogService);
  private recorridosDinamicosService = inject(RecorridosDinamicosService);
  helper = inject(HelperService);
  etiquetaTipoYFuncion = etiquetaTipoYFuncion;

  public selectedTab = 0;
  /// Mismos permisos que exige el listado para estas acciones.
  public puedeEditar = this.helper.tienePermiso('Vehículos - Editar');
  public puedeEditarRecorridos = this.helper.tienePermiso(
    'Vehículos - Editar recorridos',
  );
  public idVehiculo: string;
  public activo: IActivo;
  public loading: boolean = true;
  public chips: IPageTitleChip[] = [];

  public params?: IParamEventoUsuario;
  public paramsCertificados?: IParamCertificadoEntidad;

  /// Qué sensores reportó alguna vez el vehículo: las pestañas de Combustible
  /// y Temperatura se muestran solo si hay. Hay un último reporte por tipo.
  public sensores = { combustible: false, temperatura: false };

  private async detectarSensores(): Promise<void> {
    try {
      const data = await firstValueFrom(
        this.lista2.ultimosReportesGenericos(
          {
            filter: JSON.stringify({ idsAsignados: this.idVehiculo }),
            select: 'tipoReporte',
            includeChildren: true,
          },
          0,
        ),
      );
      const tipos = new Set(data.datos.map((r) => r.tipoReporte));
      this.sensores = {
        combustible: tipos.has('Tracker 4G Combustible'),
        temperatura: tipos.has('Tracker 4G Temperatura'),
      };
    } catch (error) {
      // Ante la duda se muestran: mejor una pestaña vacía que una que falta.
      console.error('Error al detectar los sensores del vehículo', error);
      this.sensores = { combustible: true, temperatura: true };
    }
  }

  /** Clase del chip del estado operativo (colores semánticos de la marca). */
  public claseEstado(estado: string): string {
    if (estado === 'Operativo') return 'ok';
    if (estado === 'En mantenimiento') return 'advertencia';
    if (estado === 'Fuera de servicio') return 'alerta';
    return 'neutro';
  }

  public tabChanged(event: MatTabChangeEvent): void {
    // Setea el query param  'tab' con el valor del tab seleccionado
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.selectedTab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async listar() {
    this.activo = await firstValueFrom(this.lista2.vehiculo(this.idVehiculo));
  }

  public setChips() {
    const chips: IPageTitleChip[] = [];

    if (this.activo?.cliente?.nombre) {
      chips.push({
        label: this.activo.cliente.nombre,
        icon: 'business',
      });
    }

    if (!this.activo?.fechaAlta) {
      chips.push({
        label: 'Sin Alta',
        class: 'sin-alta-chip',
      });
    }

    this.chips = chips;
  }

  public editar(): void {
    this.paramsService.setParams({ vehiculo: this.activo });
    this.router.navigate(['main/vehiculos/vehiculos/editar']);
  }

  public crearRegla(): void {
    if (!this.activo) return;
    CrearReglaVehiculoComponent.open(this.formularios, { activo: this.activo });
  }

  /**
   * Da por terminado el recorrido dinámico asignado ahora mismo: calcula y
   * persiste el cumplimiento definitivo, y libera al vehículo para poder
   * asignarle otro sin esperar a que pase el tiempo ni borrar este.
   */
  public async finalizarRecorridoDinamico(): Promise<void> {
    const id = this.activo?.vehiculo?.idRecorridoDinamico;
    if (!id) return;
    const res = await this.dialogService.confirm(
      'Finalizar recorrido dinámico',
      `¿Dar por terminado el recorrido dinámico asignado a ${this.activo.identificacion}? Se calcula el cumplimiento final y el vehículo queda libre para asignarle otro.`,
      'Finalizar',
      'Cancelar',
    );
    if (!res) return;
    try {
      await this.recorridosDinamicosService.finalizar(id);
      this.helper.notifSuccess('Recorrido dinámico finalizado');
      await this.listar();
      this.setChips();
    } catch (error) {
      this.helper.notifError(error);
    }
  }

  public puedeCrearRegla(): boolean {
    return this.helper.tienePermiso(
      'Administración - Crear eventos personalizados',
    );
  }

  public verEnMapa(): void {
    if (!this.activo?._id) return;
    this.mapaService.setPendingVehicleActivoId(this.activo._id);
    this.router.navigate(['main/vehiculos/mapa']);
  }

  public botonCrearMostrar(): boolean {
    return HelperService.getConfigCliente().moduloActivos?.crearDispositivos
      ? true
      : false;
  }

  async ngOnInit(): Promise<void> {
    const params = await firstValueFrom(this.activatedRoute.paramMap);
    this.idVehiculo = params.get('id');

    await Promise.all([this.listar(), this.detectarSensores()]);
    this.setChips();

    // Los parametros para el componente config evento usuario
    this.params = {
      tipoEntidad: 'Vehiculo',
      agrupacion: 'Entidad',
      idEntidad: this.idVehiculo,
      botonCrear:
        HelperService.getConfigCliente().moduloVehiculos?.crearVehiculos,
      activo: this.activo,
      crear: 'Dialog',
    };
    this.paramsCertificados = {
      idEntidad: this.idVehiculo,
      botonCrear:
        HelperService.getConfigCliente().moduloVehiculos?.crearVehiculos,
      crear: 'Dialog',
    };

    //
    // Obtiene el query param 'tab' y lo asigna al tab seleccionado
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['tab']) {
        this.selectedTab = parseInt(params['tab']);
      }
    });
    this.loading = false;
  }
}
